import React, { useState } from 'react';
import {
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    TableSortLabel,
    IconButton,
    Box,
    Typography,
    Tooltip
} from '@mui/material';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import KeyboardArrowRightIcon from '@mui/icons-material/KeyboardArrowRight';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import { formatWeekHeader, isDatePeriod } from '../Utilities/weekNumberUtils';
import { useColumnWidths } from '../Utilities/columnWidthUtils';
import { resolveAccuracyPeriod } from '../Utilities/forecastAccuracyView';

interface ForecastPeriod {
    forecastPeriodId: number;
    periodIndex: number;
    periodName: string;
}

interface Forecast {
    forecastId: number;
    name: string;
    startDate: string;
    forecastPeriods: ForecastPeriod[];
}

interface PeriodData {
    forecast: number | null;
    actual: number | null;
    variance: number | null;
    absoluteVariance: number | null;
    forecastUnits?: number | null;
    actualUnits?: number | null;
    forecastRevenue?: number | null;
    actualRevenue?: number | null;
}

interface ChildRow {
    id: string;
    customerName?: string;
    sku?: string;
    description?: string;
    periods: PeriodData[];
}

interface AccuracyRow {
    id: string;
    customerName?: string;
    sku?: string;
    description?: string;
    periods: PeriodData[];
    children?: ChildRow[];
}

interface ForecastAccuracyData {
    periodNames: string[];
    rows: AccuracyRow[];
}

interface ForecastAccuracyGridProps {
    data: ForecastAccuracyData | null;
    selectedForecast?: Forecast;
    aggregationPeriod: 'monthly' | 'quarterly' | 'weekly';
    viewMode: 'revenue' | 'units';
    groupBy: 'customer' | 'sku';
    varianceMode: 'percent' | 'absolute';
}

type SortOrder = 'asc' | 'desc';
type OrderByField = 'customerName' | 'sku' | 'description' | 'fy';

const EXPAND_COLUMN_WIDTH = 50;
const CUSTOMER_NAME_COLUMN_WIDTH = 200;
const SKU_COLUMN_WIDTH = 120;
const DESCRIPTION_COLUMN_WIDTH = 250;
const MIN_PINNED_COLUMN_WIDTH = 80;
const MAX_PINNED_COLUMN_WIDTH = 480;
const PINNED_COLUMN_RESIZE_STEP = 10;
const LAST_PINNED_COLUMN_SHADOW = '2px 0 4px rgba(0,0,0,0.1)';
const PINNED_COLUMN_WIDTHS_STORAGE_KEY = 'forecastAccuracy';

const DEFAULT_PINNED_COLUMN_WIDTHS = {
    customerName: CUSTOMER_NAME_COLUMN_WIDTH,
    sku: SKU_COLUMN_WIDTH,
    description: DESCRIPTION_COLUMN_WIDTH,
};

const clampPinnedColumnWidth = (width: number) => {
    if (!Number.isFinite(width)) {
        return MIN_PINNED_COLUMN_WIDTH;
    }
    return Math.min(MAX_PINNED_COLUMN_WIDTH, Math.max(MIN_PINNED_COLUMN_WIDTH, Math.round(width)));
};

interface PinnedColumnResizeHandleProps {
    label: string;
    width: number;
    onResize: (width: number) => void;
}

const PinnedColumnResizeHandle: React.FC<PinnedColumnResizeHandleProps> = ({ label, width, onResize }) => {
    const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        if (!Number.isFinite(event.clientX)) {
            return;
        }

        const startX = event.clientX;
        const startWidth = width;
        let lastApplied = startWidth;

        const handleMove = (moveEvent: PointerEvent) => {
            if (!Number.isFinite(moveEvent.clientX)) {
                return;
            }
            const nextWidth = clampPinnedColumnWidth(startWidth + moveEvent.clientX - startX);
            if (nextWidth !== lastApplied) {
                lastApplied = nextWidth;
                onResize(nextWidth);
            }
        };

        const handleUp = () => {
            window.removeEventListener('pointermove', handleMove);
            window.removeEventListener('pointerup', handleUp);
            window.removeEventListener('pointercancel', handleUp);
        };

        window.addEventListener('pointermove', handleMove);
        window.addEventListener('pointerup', handleUp);
        window.addEventListener('pointercancel', handleUp);
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'ArrowLeft') {
            event.preventDefault();
            onResize(clampPinnedColumnWidth(width - PINNED_COLUMN_RESIZE_STEP));
        } else if (event.key === 'ArrowRight') {
            event.preventDefault();
            onResize(clampPinnedColumnWidth(width + PINNED_COLUMN_RESIZE_STEP));
        }
    };

    return (
        <Box
            role="separator"
            aria-orientation="vertical"
            aria-label={`Resize ${label} column`}
            aria-valuenow={width}
            aria-valuemin={MIN_PINNED_COLUMN_WIDTH}
            aria-valuemax={MAX_PINNED_COLUMN_WIDTH}
            tabIndex={0}
            onPointerDown={handlePointerDown}
            onKeyDown={handleKeyDown}
            onClick={(event) => event.stopPropagation()}
            sx={{
                position: 'absolute',
                top: 0,
                right: 0,
                width: 10,
                height: '100%',
                cursor: 'col-resize',
                zIndex: 4,
                touchAction: 'none',
                userSelect: 'none',
                '&::after': {
                    content: '""',
                    position: 'absolute',
                    top: 8,
                    bottom: 8,
                    right: 3,
                    width: 2,
                    borderRadius: 1,
                    backgroundColor: 'transparent',
                },
                '&:hover::after, &:focus-visible::after': {
                    backgroundColor: 'primary.main',
                },
            }}
        />
    );
};

interface PinnedTableCellProps {
    left: number;
    width: number;
    isLastPinned?: boolean;
    isHeader?: boolean;
    constrainContent?: boolean;
    indent?: boolean;
    colSpan?: number;
    component?: 'td' | 'th';
    scope?: string;
    resizeLabel?: string;
    onResize?: (width: number) => void;
    children?: React.ReactNode;
}

const PinnedTableCell: React.FC<PinnedTableCellProps> = ({
    left,
    width,
    isLastPinned = false,
    isHeader = false,
    constrainContent = true,
    indent = false,
    colSpan,
    component,
    scope,
    resizeLabel,
    onResize,
    children,
}) => (
    <TableCell
        component={component}
        scope={scope}
        colSpan={colSpan}
        className="sticky-cell"
        style={{
            position: 'sticky',
            left,
            width,
            minWidth: width,
            maxWidth: width,
            overflow: 'hidden',
            boxSizing: 'border-box',
            ...(constrainContent ? { padding: 0 } : undefined),
        }}
        sx={{
            zIndex: isHeader ? 3 : 2,
            backgroundColor: (theme) => theme.palette.background.paper,
            boxShadow: isLastPinned ? LAST_PINNED_COLUMN_SHADOW : 'none',
            ...(!constrainContent ? { pl: indent ? 2 : 0.75 } : {}),
        }}
    >
        {constrainContent ? (
            <Box
                component="span"
                data-pinned-content=""
                style={{
                    display: 'block',
                    width,
                    maxWidth: width,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    boxSizing: 'border-box',
                }}
                sx={{
                    px: 2,
                    py: 1,
                    ...(indent ? { pl: 4 } : {}),
                }}
            >
                {children}
            </Box>
        ) : (
            children
        )}
        {isHeader && onResize && resizeLabel && (
            <PinnedColumnResizeHandle
                label={resizeLabel}
                width={width}
                onResize={onResize}
            />
        )}
    </TableCell>
);

const ForecastAccuracyGrid: React.FC<ForecastAccuracyGridProps> = ({ 
    data, 
    selectedForecast, 
    aggregationPeriod, 
    viewMode, 
    groupBy, 
    varianceMode 
}) => {
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [orderBy, setOrderBy] = useState<OrderByField>('fy');
    const [order, setOrder] = useState<SortOrder>('desc');
    const [expandedRows, setExpandedRows] = useState<Set<string>>(new Set());
    const { columnWidths, handleColumnResize } = useColumnWidths(
        PINNED_COLUMN_WIDTHS_STORAGE_KEY,
        DEFAULT_PINNED_COLUMN_WIDTHS
    );

    const customerNameWidth = clampPinnedColumnWidth(columnWidths.customerName ?? CUSTOMER_NAME_COLUMN_WIDTH);
    const skuWidth = clampPinnedColumnWidth(columnWidths.sku ?? SKU_COLUMN_WIDTH);
    const descriptionWidth = clampPinnedColumnWidth(columnWidths.description ?? DESCRIPTION_COLUMN_WIDTH);

    const resizePinnedColumn = (field: keyof typeof DEFAULT_PINNED_COLUMN_WIDTHS) => (width: number) => {
        handleColumnResize({ colDef: { field }, width });
    };

    if (!data || !data.rows || data.rows.length === 0) {
        return (
            <Paper sx={{ p: 3 }}>
                <Typography>No data available</Typography>
            </Paper>
        );
    }

    const childFySortValue = (child: ChildRow): number => {
        const fyPeriod = child.periods[child.periods.length - 1];
        return fyPeriod?.actual ?? fyPeriod?.forecast ?? 0;
    };

    const resolveRowPeriods = <T extends { periods: PeriodData[]; children?: ChildRow[] }>(row: T): T => ({
        ...row,
        periods: row.periods.map((period) => resolveAccuracyPeriod(period, viewMode)),
        children: row.children
            ?.map((child) => ({
                ...child,
                periods: child.periods.map((period) => resolveAccuracyPeriod(period, viewMode))
            }))
            .sort((a, b) => childFySortValue(b) - childFySortValue(a))
    });

    const viewRows = data.rows.map(resolveRowPeriods);

    const handleChangePage = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };

    const handleRequestSort = (property: OrderByField) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleRowExpand = (rowId: string) => {
        const newExpanded = new Set(expandedRows);
        if (newExpanded.has(rowId)) {
            newExpanded.delete(rowId);
        } else {
            newExpanded.add(rowId);
        }
        setExpandedRows(newExpanded);
    };

    const formatValue = (value: number | null | undefined): string => {
        if (value === null || value === undefined) return '-';
        if (viewMode === 'revenue') {
            return `£${value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
        }
        return value.toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
    };

    const formatVariance = (variance: number | null | undefined, absoluteVariance: number | null | undefined): string => {
        if (varianceMode === 'absolute') {
            if (absoluteVariance === null || absoluteVariance === undefined) return '-';
            const formatted = viewMode === 'revenue'
                ? `£${Math.abs(absoluteVariance).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`
                : Math.abs(absoluteVariance).toLocaleString('en-GB', { minimumFractionDigits: 0, maximumFractionDigits: 0 });
            return absoluteVariance >= 0 ? `+${formatted}` : `-${formatted}`;
        } else {
            if (variance === null || variance === undefined) return '-';
            const formatted = Math.abs(variance).toFixed(1);
            return variance >= 0 ? `+${formatted}%` : `-${formatted}%`;
        }
    };

    const getVarianceColor = (variance: number | null | undefined, absoluteVariance: number | null | undefined): string => {
        const value = varianceMode === 'absolute' ? absoluteVariance : variance;
        if (value === null || value === undefined) return 'text.secondary';
        if (value >= 0) return 'success.main';
        return 'error.main';
    };

    const rawPeriodNames = data.periodNames || [];
    const basePeriodNames = rawPeriodNames.filter(name => name !== 'FY');
    
    const periodNamesWithFY = [...basePeriodNames, 'FY'];

    const periodNames = aggregationPeriod === "weekly" 
        ? periodNamesWithFY.map((period, index) => {
            if (/^\d{2}-\d{2}-\d{2,4}$/.test(period)) {
                return formatWeekHeader(period, index);
            }
            return period;
        })
        : periodNamesWithFY;

    const sortData = (rows: AccuracyRow[]): AccuracyRow[] => {
        return [...rows].sort((a, b) => {
            let aValue: string | number;
            let bValue: string | number;

            if (orderBy === 'fy') {
                const aFyPeriod = a.periods[a.periods.length - 1];
                const bFyPeriod = b.periods[b.periods.length - 1];
                aValue = (viewMode === 'revenue' ? aFyPeriod?.actual : aFyPeriod?.actual) || 0;
                bValue = (viewMode === 'revenue' ? bFyPeriod?.actual : bFyPeriod?.actual) || 0;
            } else {
                aValue = (a as any)[orderBy] || '';
                bValue = (b as any)[orderBy] || '';

                if (orderBy === 'customerName' || orderBy === 'sku' || orderBy === 'description') {
                    aValue = (aValue as string).toLowerCase();
                    bValue = (bValue as string).toLowerCase();
                } else {
                    aValue = aValue || 0;
                    bValue = bValue || 0;
                }
            }

            if (aValue < bValue) {
                return order === 'asc' ? -1 : 1;
            }
            if (aValue > bValue) {
                return order === 'asc' ? 1 : -1;
            }
            return 0;
        });
    };

    const sortedRows = sortData(viewRows);
    const paginatedRows = sortedRows.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

    const copyToClipboard = async () => {
        if (!data || !data.rows || data.rows.length === 0) return;

        let csvContent = '';
        
        const headerRow = groupBy === 'customer' ? ['Customer Name'] : ['SKU', 'Description'];
        
        periodNames.forEach(period => {
            const periodLabel = period.replace('\n', ' ');
            headerRow.push(`${periodLabel} Forecast`);
            headerRow.push(`${periodLabel} Actual`);
            headerRow.push(`${periodLabel} Difference`);
        });
        
        csvContent += headerRow.join('\t') + '\n';

        const processRow = (row: AccuracyRow | ChildRow, isChild = false): string => {
            const rowData: string[] = [];
            
            if (groupBy === 'customer') {
                if (isChild) {
                    const sku = (row as ChildRow).sku || '';
                    const description = (row as ChildRow).description || '';
                    rowData.push(`${sku} - ${description}`);
                } else {
                    rowData.push((row as AccuracyRow).customerName || '');
                }
            } else {
                if (isChild) {
                    rowData.push('');
                    rowData.push((row as ChildRow).customerName || '');
                } else {
                    rowData.push((row as AccuracyRow).sku || '');
                    rowData.push((row as AccuracyRow).description || '');
                }
            }

            const periods = row.periods || [];
            const basePeriodCount = periods.length - 1;
            
            for (let i = 0; i < basePeriodCount; i++) {
                const period = periods[i];
                if (period) {
                    rowData.push(period.forecast?.toFixed(2) || '');
                    rowData.push(period.actual?.toFixed(2) || '');
                    rowData.push(period.absoluteVariance?.toFixed(2) || '');
                } else {
                    rowData.push('', '', '');
                }
            }

            const fyPeriod = periods[basePeriodCount];
            if (fyPeriod) {
                rowData.push(fyPeriod.forecast?.toFixed(2) || '');
                rowData.push(fyPeriod.actual?.toFixed(2) || '');
                rowData.push(fyPeriod.absoluteVariance?.toFixed(2) || '');
            } else {
                rowData.push('', '', '');
            }

            return rowData.join('\t');
        };

        sortedRows.forEach(row => {
            csvContent += processRow(row, false) + '\n';
            
            if (row.children && row.children.length > 0) {
                row.children.forEach(child => {
                    csvContent += processRow(child, true) + '\n';
                });
            }
        });

        try {
            await navigator.clipboard.writeText(csvContent);
        } catch (err) {
            console.error('Failed to copy to clipboard:', err);
        }
    };

    const renderPeriodCell = (periodData: PeriodData | null) => {
        if (!periodData) return <TableCell align="right">-</TableCell>;
        
        return (
            <TableCell align="right">
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', lineHeight: 1.2 }}>
                    <Typography
                        variant="caption"
                        component="span"
                        color="text.secondary"
                        sx={{ display: 'block', whiteSpace: 'nowrap', fontSize: '0.9rem' }}
                    >
                        {formatValue(periodData.forecast)}
                    </Typography>
                    <Typography 
                        variant="body2"
                        sx={{ color: 'primary.main', fontWeight: 'bold' }}
                    >
                        {formatValue(periodData.actual)}
                    </Typography>
                    <Typography
                        variant="caption"
                        component="span"
                        sx={{ display: 'block', whiteSpace: 'nowrap', color: getVarianceColor(periodData.variance, periodData.absoluteVariance) }}
                    >
                        {formatVariance(periodData.variance, periodData.absoluteVariance)}
                    </Typography>
                </Box>
            </TableCell>
        );
    };

    const renderRow = (row: AccuracyRow | ChildRow, isChild = false) => {
        const isExpanded = expandedRows.has(row.id);
        const hasChildren = 'children' in row && row.children && row.children.length > 0 && !isChild;

        const periods = row.periods || [];
        const basePeriodCount = periods.length - 1;
        const basePeriods = periods.slice(0, basePeriodCount);
        const fyPeriod = periods[basePeriodCount];

        return (
            <TableRow 
                key={row.id}
                sx={{ 
                    backgroundColor: isChild ? 'rgba(0, 0, 0, 0.02)' : 'inherit',
                    cursor: hasChildren ? 'pointer' : 'default',
                    '&:hover': hasChildren ? {
                        backgroundColor: 'rgba(0, 0, 0, 0.04)'
                    } : {},
                    '&:hover > th, &:hover > .sticky-cell': {
                        backgroundColor: (theme) => theme.palette.mode === 'dark'
                            ? 'rgba(30, 30, 30, 1)'
                            : 'rgba(245, 245, 245, 1)'
                    }
                }}
                onClick={hasChildren ? () => handleRowExpand(row.id) : undefined}
            >
                <PinnedTableCell
                    left={0}
                    width={EXPAND_COLUMN_WIDTH}
                    constrainContent={false}
                    indent={isChild}
                >
                    {hasChildren && (
                        <IconButton
                            aria-label="expand row"
                            size="small"
                            onClick={(e) => {
                                e.stopPropagation();
                                handleRowExpand(row.id);
                            }}
                            sx={{ p: 0 }}
                        >
                            {isExpanded ? <KeyboardArrowDownIcon /> : <KeyboardArrowRightIcon />}
                        </IconButton>
                    )}
                </PinnedTableCell>
                {groupBy === 'customer' && (
                    <PinnedTableCell
                        component="th"
                        scope="row"
                        colSpan={2}
                        left={EXPAND_COLUMN_WIDTH}
                        width={customerNameWidth}
                        isLastPinned
                        indent={isChild}
                    >
                        {isChild ? `${(row as ChildRow).sku} - ${(row as ChildRow).description}` : (row as AccuracyRow).customerName}
                    </PinnedTableCell>
                )}
                {groupBy === 'sku' && (
                    <>
                        <PinnedTableCell
                            component="th"
                            scope="row"
                            colSpan={isChild ? 2 : 1}
                            left={EXPAND_COLUMN_WIDTH}
                            width={isChild ? skuWidth + descriptionWidth : skuWidth}
                            isLastPinned={isChild}
                            indent={isChild}
                        >
                            {isChild ? (row as ChildRow).customerName : (row as AccuracyRow).sku}
                        </PinnedTableCell>
                        {!isChild && (
                            <PinnedTableCell
                                left={EXPAND_COLUMN_WIDTH + skuWidth}
                                width={descriptionWidth}
                                isLastPinned
                            >
                                {(row as AccuracyRow).description}
                            </PinnedTableCell>
                        )}
                    </>
                )}
                {basePeriods.map((periodData, index) => (
                    <React.Fragment key={index}>
                        {renderPeriodCell(periodData)}
                    </React.Fragment>
                ))}
                {fyPeriod && renderPeriodCell(fyPeriod)}
            </TableRow>
        );
    };

    const renderRows = () => {
        const rows: React.ReactElement[] = [];
        paginatedRows.forEach((row) => {
            rows.push(renderRow(row, false));
            
            if (row.children && row.children.length > 0 && expandedRows.has(row.id)) {
                row.children.forEach((child) => {
                    rows.push(renderRow(child, true));
                });
            }
        });
        return rows;
    };

    return (
        <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Paper elevation={1} sx={{ p: 2, display: 'flex', gap: 2, alignItems: 'center', borderBottom: 1, borderColor: 'divider', flexShrink: 0 }}>
                <Tooltip title="Copy table to clipboard">
                    <IconButton
                        onClick={copyToClipboard}
                        size="small"
                        sx={{ p: 0.5 }}
                    >
                        <ContentCopyIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Paper>
            <TableContainer 
                component={Paper}
                sx={{ 
                    height: '100%',
                    overflow: 'auto',
                    maxWidth: '100%',
                    display: 'inline-block',
                    width: 'fit-content',
                    minWidth: '100%',
                    flexGrow: 1
                }}
            >
                <Table 
                    stickyHeader 
                    aria-label="forecast accuracy table"
                    sx={{ 
                        tableLayout: 'auto', 
                        width: 'auto',
                        '& .MuiTableBody-root .MuiTableCell-root': {
                            whiteSpace: 'nowrap'
                        },
                        '& .MuiTableHead-root .MuiTableCell-root': {
                            whiteSpace: 'nowrap'
                        },
                        '& .MuiTableHead-root .MuiTableCell-root[data-week-header="true"]': {
                            whiteSpace: 'pre'
                        }
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <PinnedTableCell
                                left={0}
                                width={EXPAND_COLUMN_WIDTH}
                                isHeader
                                constrainContent={false}
                            />
                            {groupBy === 'customer' && (
                                <PinnedTableCell
                                    colSpan={2}
                                    left={EXPAND_COLUMN_WIDTH}
                                    width={customerNameWidth}
                                    isLastPinned
                                    isHeader
                                    resizeLabel="Customer Name"
                                    onResize={resizePinnedColumn('customerName')}
                                >
                                    <TableSortLabel
                                        active={orderBy === 'customerName'}
                                        direction={orderBy === 'customerName' ? order : 'asc'}
                                        onClick={() => handleRequestSort('customerName')}
                                    >
                                        Customer Name
                                    </TableSortLabel>
                                </PinnedTableCell>
                            )}
                            {groupBy === 'sku' && (
                                <>
                                    <PinnedTableCell
                                        left={EXPAND_COLUMN_WIDTH}
                                        width={skuWidth}
                                        isHeader
                                        resizeLabel="SKU"
                                        onResize={resizePinnedColumn('sku')}
                                    >
                                        <TableSortLabel
                                            active={orderBy === 'sku'}
                                            direction={orderBy === 'sku' ? order : 'asc'}
                                            onClick={() => handleRequestSort('sku')}
                                        >
                                            SKU
                                        </TableSortLabel>
                                    </PinnedTableCell>
                                    <PinnedTableCell
                                        left={EXPAND_COLUMN_WIDTH + skuWidth}
                                        width={descriptionWidth}
                                        isLastPinned
                                        isHeader
                                        resizeLabel="Description"
                                        onResize={resizePinnedColumn('description')}
                                    >
                                        <TableSortLabel
                                            active={orderBy === 'description'}
                                            direction={orderBy === 'description' ? order : 'asc'}
                                            onClick={() => handleRequestSort('description')}
                                        >
                                            Description
                                        </TableSortLabel>
                                    </PinnedTableCell>
                                </>
                            )}
                            {periodNames.map((period, index) => {
                                const isWeekHeader = aggregationPeriod === "weekly" && isDatePeriod(period);
                                const isFyColumn = period === 'FY';
                                return (
                                    <TableCell
                                        key={index}
                                        align="right"
                                        sx={{
                                            whiteSpace: isWeekHeader ? 'pre' : 'nowrap',
                                            backgroundColor: (theme) => theme.palette.background.paper,
                                            zIndex: 1
                                        }}
                                        data-week-header={isWeekHeader}
                                    >
                                        {isFyColumn ? (
                                            <TableSortLabel
                                                active={orderBy === 'fy'}
                                                direction={orderBy === 'fy' ? order : 'desc'}
                                                onClick={() => handleRequestSort('fy')}
                                            >
                                                {period.toUpperCase()}
                                            </TableSortLabel>
                                        ) : (
                                            period.toUpperCase()
                                        )}
                                    </TableCell>
                                );
                            })}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {renderRows()}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                rowsPerPageOptions={[10, 25, 50, 100]}
                component="div"
                count={sortedRows.length}
                rowsPerPage={rowsPerPage}
                page={page}
                onPageChange={handleChangePage}
                onRowsPerPageChange={handleChangeRowsPerPage}
                sx={{ flexShrink: 0 }}
            />
        </Box>
    );
};

export default ForecastAccuracyGrid;

